import React, { CSSProperties, useEffect, useState } from "react"
import styles from "./ProjectDetails.module.css"
import { AIProject, CanvasCell, CellField, CellType, FieldDescriptions } from "../Model/types"
import { getCanvasCells } from "../Model/getCanvasCells"
import { updateLeanCanvasRecord } from "../Utils/updateLeanCanvasRecord"
import ListBox from "./ValueBoxes/ListBox"
import TextBox from "./ValueBoxes/TextBox" // Import the new TextBox component

import { styled, Switch } from "@mui/material"
import NumberBox from "./ValueBoxes/NumberBox"
import { useLocation } from "react-router-dom"
import SelectionBox from "./ValueBoxes/SelectionBox"
import logo from "../../Assets/AI-COE-Logo.png"
import MarkdownTooltip from "./MarkdownTooltip"
import MultiSelectBox from "./ValueBoxes/MultiSelectBox"
import SignOut from "../../Components/SignOut"
import Color from "color"

interface ProjectDetailsProps {
    project: AIProject
    expectedBenefitsData: any[]
    endCustomersData: any[]
    businessValueData: any[]
    onDataUpdate: () => void
    fieldDescriptions: FieldDescriptions
}

const ProjectDetails: React.FC<ProjectDetailsProps> = ({ project, expectedBenefitsData, endCustomersData, businessValueData, onDataUpdate, fieldDescriptions }) => {
    const location = useLocation()
    const searchParams = new URLSearchParams(location.search)
    const edit = searchParams.get("edit")
    const [canEdit, setCanEdit] = useState(false)

    const cellData = getCanvasCells(project)

    useEffect(() => {
        //
    }, [edit])

    const onEditSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCanEdit(event.target.checked)
    }

    const onTextDoneEditing = (fieldName: string, newValue: string) => {
        updateLeanCanvasRecord(project.id, { [fieldName]: newValue }).then(() => onDataUpdate())
    }

    const onListValueChange = (fieldName: string, valueArray: string[]) => {
        updateLeanCanvasRecord(project.id, { [fieldName]: valueArray }).then(() => onDataUpdate())
    }

    const onNumberDoneEditing = (fieldName: string, newValue: number) => {
        updateLeanCanvasRecord(project.id, { [fieldName]: newValue }).then(() => onDataUpdate())
    }

    const onSelectValueChange = (fieldName: string, newValue: string) => {
        const costRecordId = expectedBenefitsData.find((data) => data.fields.Amount === newValue).id

        if (costRecordId) {
            updateLeanCanvasRecord(project.id, { [fieldName]: [costRecordId] }).then(() => onDataUpdate())
        }
    }

    const onMultiSelectValueChange = (fieldName: string, valueArray: string[]) => {
        if (fieldName === CellField.EndCustomers) {
            const ids = valueArray.map((name) => {
                const data = endCustomersData.find((item) => item.fields.Option === name)
                const id = data.id
                return id
            })

            updateLeanCanvasRecord(project.id, { [fieldName]: ids }).then(() => onDataUpdate())
        }

        if (fieldName === CellField.BusinessValue) {
            const ids = valueArray.map((name) => {
                const data = businessValueData.find((item) => item.fields.Option === name)
                const id = data.id
                return id
            })

            updateLeanCanvasRecord(project.id, { [fieldName]: ids }).then(() => onDataUpdate())
        }
    }

    const countItems = (items: any | any[]): number => {
        if (Array.isArray(items)) {
            return items.reduce((acc, item) => acc + countItems(item), 0)
        } else {
            return 1
        }
    }

    let inputColors = ["#61C6A9", "#438CD3", "#5743CC", "#AA0086", "#EABB02"]
    inputColors.reverse()
    const steps = countItems(cellData)
    const colors = generateGradientColors(inputColors, steps)

    const costOptions = expectedBenefitsData.map((item) => item.fields["Amount"]).sort()
    const endCustomerOptions = endCustomersData.map((item) => item.fields["Option"]).sort()
    const businessValueOptions = businessValueData.map((item) => item.fields["Option"]).sort()

    function getCellBox(canvasCell: CanvasCell, maxCharCount: number) {
        let box: JSX.Element | null = null
        switch (canvasCell.type) {
            case CellType.Text:
                box = <TextBox data={{ fieldName: canvasCell.fieldName, value: canvasCell.value as string }} onDoneEditing={onTextDoneEditing} canEdit={canEdit} maxLength={maxCharCount} />
                break

            case CellType.List:
                box = <ListBox data={{ fieldName: canvasCell.fieldName, value: canvasCell.value as string[] }} onValueChange={onListValueChange} canEdit={canEdit} />
                break

            case CellType.Number:
                box = <NumberBox data={canvasCell} onDoneEditing={onNumberDoneEditing} canEdit={canEdit} />
                break

            case CellType.Select:
                box = <SelectionBox data={{ fieldName: canvasCell.fieldName, value: canvasCell.value as string }} options={costOptions} onValueChange={onSelectValueChange} canEdit={canEdit} />
                break

            case CellType.MultiSelect:
                let options = []
                if (canvasCell.fieldName === CellField.EndCustomers) {
                    options = endCustomerOptions
                } else if (canvasCell.fieldName === CellField.BusinessValue) {
                    options = businessValueOptions
                }
                box = <MultiSelectBox data={{ fieldName: canvasCell.fieldName, value: canvasCell.value as string[] }} options={options} onValueChange={onMultiSelectValueChange} canEdit={canEdit} />
                break

            default:
                break
        }

        return box
    }

    const cellStyle: CSSProperties = { flexBasis: "20%" }

    let count = 0

    const cells = cellData.map((canvasCell, index) => {
        const box = getCellBox(canvasCell, 2000)

        const colorStyle = { backgroundColor: colors[count] }
        count++

        return (
            <div key={`box-id-${index}`} className={styles.canvasCell} style={cellStyle}>
                <div className={styles.colorBar} style={colorStyle}></div>
                <div className={styles.cellTitle}>
                    <MarkdownTooltip title={fieldDescriptions[canvasCell.fieldName]} placement="top">
                        <div>{canvasCell.fieldName}</div>
                    </MarkdownTooltip>
                </div>
                <div className={styles.boxContainer} style={{}}>
                    {box}
                </div>
            </div>
        )
    })

    const stakeholderNames = joinSortedStrings(project.stakeholderNames)
    let requestor = project.requestorName
    if (requestor === "") {
        requestor = "--"
    }

    const titleEditBox = (
        <div className={styles.titleEditBox}>
            <TextBox data={{ fieldName: CellField.Summary, value: project.summary as string }} onDoneEditing={onTextDoneEditing} canEdit={canEdit} maxLength={200} extraStyle={{ fontSize: "36px", lineHeight: "42px", fontWeight: "bold" }} />
        </div>
    )

    return (
        <div className={styles.projectDetails} id="project-details">
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <div className={styles.title}>
                        <div>{project.jiraId}</div> | {titleEditBox}
                    </div>
                    <div className={styles.metadata}>
                        <div className={styles.people}>
                            <div>
                                <b>Requestor:</b> {requestor}
                            </div>
                            <div>
                                <b>Stakeholders:</b> {stakeholderNames}
                            </div>
                            <div>
                                <b>Status:</b> {project.status}
                            </div>
                        </div>
                        <div className={styles.links}>
                            <div
                                className={styles.link}
                                onClick={() => {
                                    window.open(project.jiraURL, "_blank")
                                }}
                            >
                                View {project.jiraId} in Jira
                            </div>
                            {project.confluenceURL && (
                                <div
                                    className={styles.link}
                                    onClick={() => {
                                        window.open(project.confluenceURL, "_blank")
                                    }}
                                >
                                    View in Confluence
                                </div>
                            )}
                            {project.teamsURL && (
                                <div
                                    className={styles.link}
                                    onClick={() => {
                                        window.open(project.teamsURL, "_blank")
                                    }}
                                >
                                    Chat in Teams
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <img src={logo} alt="AI COE" className={styles.logo} />
            </div>
            <div>
                {edit === "true" && (
                    <div className={styles.switch}>
                        Edit Mode <Switch checked={canEdit} onChange={onEditSwitchChange} />
                    </div>
                )}
                <div className={styles.boxes}>{cells}</div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <SignOut />
            </div>
        </div>
    )
}

function joinSortedStrings(stringsArray: string[]): string {
    if (stringsArray === undefined || stringsArray.length === 0) {
        return "--"
    }
    const sortedArray = [...stringsArray].sort()
    const joinedString = sortedArray.join(", ")
    return joinedString
}

export default ProjectDetails

export function generateGradientColors(colorHexArray: string[], stepCount: number): string[] {
    const gradient: string[] = []
    const totalIntervals = colorHexArray.length - 1

    for (let i = 0; i < totalIntervals; i++) {
        let stepsPerInterval = Math.floor(stepCount / totalIntervals)
        if (i === totalIntervals - 1) {
            stepsPerInterval += stepCount % totalIntervals // Adjust the last segment
        }

        const startColor = Color(colorHexArray[i])
        const endColor = Color(colorHexArray[i + 1])

        for (let step = 0; step < stepsPerInterval; step++) {
            const factor = step / stepsPerInterval // Adjust factor calculation
            const interpolatedColor = startColor.mix(endColor, factor).hex()
            gradient.push(interpolatedColor)
        }
    }

    // Adjust the gradient array to ensure it has exactly stepCount elements
    while (gradient.length > stepCount) {
        gradient.pop()
    }

    // Ensure the last color is exactly as specified
    if (gradient.length < stepCount) {
        gradient.push(colorHexArray[colorHexArray.length - 1])
    }

    return gradient
}
